import { useEffect } from 'react';

const BASE_MAX_SCALE = 1.45;
const BOTTOM_MARGIN = 16;

/**
 * The scroll-driven hero effect: the image scales up from a bottom anchor
 * so it grows upward and visually covers the wordmark as the visitor
 * scrolls past it.
 *
 * @param {object} args
 * @param {React.RefObject<HTMLElement>} args.imageRef the scaling element
 * @param {React.RefObject<HTMLElement>} args.headerRef the sticky header
 * @param {boolean} args.enabled false under reduced motion
 */
export function useHeroScrollScale({ imageRef, headerRef, enabled = true }) {
  useEffect(() => {
    if (!enabled) return undefined;
    const image = imageRef.current;
    if (!image) return undefined;

    let maxScale = BASE_MAX_SCALE;
    let ticking = false;
    let imageDocTop = 0;
    let imageHeight = 0;
    let headerHeight = 0;
    let frame = 0;

    // getBoundingClientRect() reflects the element's current transform, so
    // measuring it directly while scaled would feed the scaled position back
    // into the next scale calculation. Instead, measure its true position in
    // the document (and its true, unscaled height) with the transform
    // temporarily removed, then derive the on-screen position from those
    // fixed values and the scroll offset.
    const measure = () => {
      const previousTransform = image.style.transform;
      image.style.transform = 'none';
      const rect = image.getBoundingClientRect();
      imageDocTop = rect.top + window.scrollY;
      imageHeight = rect.height;
      image.style.transform = previousTransform;

      headerHeight = headerRef?.current?.getBoundingClientRect().height ?? 0;

      // Cap the scale so the fully-expanded image still fits inside one
      // viewport (below the header, above a small bottom margin), rather
      // than always scaling to a fixed factor that can make the expanded
      // image taller than the screen on shorter viewports. On tall
      // viewports this simply resolves back to the base factor.
      const availableHeight = window.innerHeight - headerHeight - BOTTOM_MARGIN;
      const maxScaleByHeight =
        imageHeight > 0 ? availableHeight / imageHeight : BASE_MAX_SCALE;
      maxScale = Math.min(BASE_MAX_SCALE, Math.max(1, maxScaleByHeight));
    };

    const update = () => {
      ticking = false;
      // The image scales up from a bottom anchor so it grows upward to
      // cover the wordmark above it. That same upward growth means its top
      // edge would end up (maxScale - 1) * imageHeight above where the
      // unscaled box's top would be. Left uncorrected, by the time scroll
      // reaches the point where the unscaled top hits the viewport top (and
      // header), the scaled top has already been pushed off-screen above
      // it. Shrinking the scroll range by that same shift (plus the header
      // height, so the sticky header doesn't obscure it either) makes the
      // scaled top land exactly at the bottom of the header when scale
      // finishes, so the whole image is visible right as it settles.
      const scaleShift = (maxScale - 1) * imageHeight;
      const scrollRange = Math.max(imageDocTop - headerHeight - scaleShift, 1);
      const progress = Math.min(Math.max(window.scrollY / scrollRange, 0), 1);
      const scale = 1 + progress * (maxScale - 1);
      image.style.transform = `scale(${scale.toFixed(3)})`;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        frame = requestAnimationFrame(update);
      }
    };
    const onResize = () => {
      measure();
      update();
    };

    measure();
    update();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onResize);
      image.style.transform = '';
    };
  }, [enabled, imageRef, headerRef]);
}
